/* eslint-disable import/no-cycle */
import React, { useState, useRef, useEffect } from "react";
import { string, arrayOf, shape, oneOf } from "prop-types";
import classNames from "classnames/bind";
import * as dayjs from "dayjs";
import { useUser } from "../../context";
import { DEFAULT_GATEWAY_EN, DEFAULT_GATEWAY_FR } from "../../const/const";
import { useFetchGoingToOptions } from "./Data/useFetchGoingToOptions";
import { useFetchDepartingFromOptions } from "./Data/useFetchDepartingFromOptions";
import { useFetchHotelOptions } from "./Data/useFetchHotelOptions";
import { Button } from "../Button";
import { Modal } from "../Modal";

import { InputWrapper } from "../InputWrapper";
import { MonthPicker } from "../DatePicker";
import { Dropdown } from "../Dropdown";

import * as styles from "./LPCSearch.module.scss";

const cx = classNames.bind(styles);

const LPCSearch = ({ durationOptions, lang, labels }) => {
	const [environment, setEnvironment] = useState();
	const select = { value: "", label: labels.select };
	const [toOptions, toProcessing, fetchToOptions] = useFetchGoingToOptions(lang);
	const [fromOptions, fromProcessing] = useFetchDepartingFromOptions(lang);
	const [hotelOptions, hotelProcessing, fetchHotels] = useFetchHotelOptions(lang);
	const [departingFrom, setDepartingFrom] = useState();

	const [goingTo, setGoingTo] = useState("");
	const [departureMonth, setDepartureMonth] = useState(dayjs().startOf("day").add(1, "months"));

	const durationDefaultPosition = durationOptions.findIndex(d => d.value === "7DAYS");
	const [duration, setDuration] = useState(
		durationDefaultPosition >= 0 ? durationOptions[durationDefaultPosition] : durationOptions[0]
	);
	const [hotel, setHotel] = useState(select);
	const [isOpen, setIsOpen] = useState(false);

	const inputRef = useRef();
	const fromRef = useRef();
	const toRef = useRef();
	const hotelRef = useRef();
	const durationRef = useRef();

	const { affiliate } = useUser();
	const [aliasCode, setAliasCode] = useState("btd");

	useEffect(() => {
		if (affiliate?.code) {
			setAliasCode(affiliate.code);
		}
	}, [affiliate]);

	useEffect(() => {
		const defaultValue = lang === "fr" ? DEFAULT_GATEWAY_FR : DEFAULT_GATEWAY_EN;
		setDepartingFrom(fromOptions.find(i => i.value === defaultValue));
	}, [fromOptions, lang]);

	useEffect(() => {
		setGoingTo(toOptions[0]);
	}, [toOptions, lang]);

	useEffect(() => {
		setEnvironment(window.document);
	}, []);
	const onSubmit = e => {
		if (window.innerWidth < 768) {
			window.open(
				`${process.env.GATSBY_LPC_BASE_URL}?gateway_dep=${departingFrom?.value}&dest_dep=${
					goingTo?.value
				}&no_hotel=${hotel?.value}&date_dep=${departureMonth.format("YYYYMM")}01&duration=${
					duration?.value
				}&star=&searchtype=PA&code_ag=rds&alias=${aliasCode}&all_inclusive=y&language=${lang}`
			);
		} else {
			setIsOpen(true);
		}
		e.preventDefault();
	};
	return (
		<div className={cx("backgroundWrapper")}>
			<h1>{labels.title}</h1>
			{labels.details}
			<div className={cx("formWrapper")}>
				<h2>{labels.subTitle}</h2>
				<form onSubmit={onSubmit}>
					<div className={cx("container")}>
						<div className={cx("item", "dpContainer")}>
							{environment ? (
								<InputWrapper ref={fromRef} id="from" label={labels.from}>
									{({ handleOnFocus, handleOnBlur }) => (
										<Dropdown
											loading={fromProcessing}
											id="from"
											inputRef={fromRef}
											className={cx("select")}
											iconClassName={cx("icon")}
											inputClassName={cx("selectInput")}
											selectedItem={departingFrom}
											data={[
												{
													items: fromOptions,
												},
											]}
											onFocus={() => {
												handleOnFocus();
											}}
											onBlur={() => {
												handleOnBlur();
											}}
											onChange={value => {
												if (value && value.value !== departingFrom.value) {
													setDepartingFrom(value);
													fetchToOptions(value.value);
												}
											}}
											environment={environment}
										/>
									)}
								</InputWrapper>
							) : (
								<div className={cx("placeholder")} />
							)}
						</div>
						<div className={cx("item", "gtContainer")}>
							{environment ? (
								<InputWrapper ref={toRef} id="to" label={labels.to}>
									{({ handleOnFocus, handleOnBlur }) => (
										<Dropdown
											loading={toProcessing}
											id="to"
											inputRef={toRef}
											className={cx("select")}
											iconClassName={cx("icon")}
											inputClassName={cx("selectInput")}
											selectedItem={goingTo}
											data={[
												{
													items: toOptions.map(i => ({
														...i,
														className: cx(i.group ? "group" : "single"),
													})),
												},
											]}
											onFocus={() => {
												handleOnFocus();
											}}
											onBlur={() => {
												handleOnBlur();
											}}
											onChange={value => {
												if (value && value.value !== goingTo.value) {
													setGoingTo(value);
													fetchHotels(departingFrom.value, value.value);
												}
											}}
											environment={environment}
										/>
									)}
								</InputWrapper>
							) : (
								<div className={cx("placeholder")} />
							)}
						</div>
						<div className={cx("item", "hoContainer")}>
							{environment ? (
								<InputWrapper ref={hotelRef} id="hotel" label={labels.hotel}>
									{({ handleOnFocus, handleOnBlur }) => (
										<Dropdown
											id="hotel"
											loading={hotelProcessing}
											inputRef={hotelRef}
											selectedItem={hotel}
											className={cx("select")}
											iconClassName={cx("icon")}
											inputClassName={cx("selectInput")}
											data={[
												{
													items: [select, ...hotelOptions],
												},
											]}
											onFocus={() => {
												handleOnFocus();
											}}
											onBlur={() => {
												handleOnBlur();
											}}
											onChange={value => {
												if (value.value !== hotel.value) {
													setHotel(value);
												}
											}}
											environment={environment}
										/>
									)}
								</InputWrapper>
							) : (
								<div className={cx("placeholder")} />
							)}
						</div>
						<div className={cx("item", "dmContainer")}>
							{environment ? (
								<InputWrapper ref={inputRef} id="date" label={labels.departing}>
									{({ handleOnFocus, handleOnBlur }) => (
										<MonthPicker
											id="date"
											inputRef={inputRef}
											selectedDate={departureMonth}
											minDate={dayjs().startOf("day")}
											maxDate={dayjs().startOf("day").add(365, "days")}
											onFocus={() => {
												handleOnFocus();
											}}
											onBlur={() => {
												handleOnBlur();
											}}
											onChange={value => {
												setDepartureMonth(value);
											}}
											format="MMM YYYY"
										/>
									)}
								</InputWrapper>
							) : (
								<div className={cx("placeholder")} />
							)}
						</div>
						<div className={cx("item", "duContainer")}>
							{environment ? (
								<InputWrapper ref={durationRef} id="duration" label={labels.goingFor}>
									{({ handleOnFocus, handleOnBlur }) => (
										<Dropdown
											id="duration"
											inputRef={durationRef}
											selectedItem={duration}
											className={cx("select")}
											iconClassName={cx("icon")}
											data={[
												{
													items: durationOptions,
												},
											]}
											onFocus={() => {
												handleOnFocus();
											}}
											onBlur={() => {
												handleOnBlur();
											}}
											onChange={value => {
												if (value.value !== duration.value) {
													setDuration(value);
												}
											}}
											environment={environment}
										/>
									)}
								</InputWrapper>
							) : (
								<div className={cx("placeholder")} />
							)}
						</div>
						<div className={cx("item", "srcContainer")}>
							{environment ? (
								<Button type="submit" label={labels.search} />
							) : (
								<div className={cx("placeholder")} />
							)}
						</div>
					</div>
				</form>
			</div>
			<Modal
				contentLabel="content details"
				isOpen={isOpen}
				container="fluid"
				className={cx("modal")}
				onClose={() => {
					setIsOpen(false);
				}}>
				<Modal.Content className={cx("iframeContainer")}>
					{isOpen && (
						<iframe
							src={`${process.env.GATSBY_LPC_BASE_URL}?gateway_dep=${
								departingFrom?.value
							}&dest_dep=${goingTo?.value}&no_hotel=${
								hotel?.value
							}&date_dep=${departureMonth.format("YYYYMM")}01&duration=${
								duration?.value
							}&star=&searchtype=PA&code_ag=rds&alias=${aliasCode}&all_inclusive=y&language=${lang}`}
							title="Lowest price calendar"
							width="960"
							height="725"
						/>
					)}
				</Modal.Content>
			</Modal>
		</div>
	);
};

LPCSearch.propTypes = {
	durationOptions: arrayOf(
		shape({
			label: string,
			value: string,
		})
	).isRequired,
	labels: shape({
		title: string,
		details: string,
		select: string,
		from: string,
		to: string,
		hotel: string,
		departing: string,
		goingFor: string,
		search: string,
	}).isRequired,
	lang: oneOf(["en", "fr"]),
};
LPCSearch.defaultProps = {
	lang: "en",
};
export default LPCSearch;
export { LPCSearch };
